import { get_token } from "@/router";
import { append_active_organization_id, get_active_organization_id } from "@/utils/organization_profile";
import { profile_matches_type } from "@/components/HomeNavBar/home_nav_helpers";
import homeJourneyDonationStats from "./home_journey_donation_stats";

const homeJourneyStats = {
  ...homeJourneyDonationStats,
  get_card_stats(key) {
    return Reflect.get(this.home_journey_stats, key) || [];
  },
  set_card_stats(key, stats = []) {
    this.home_journey_stats = {
      ...this.home_journey_stats,
      [key]: stats.filter(stat => Number(stat.value) > 0),
    };
  },
  get_json_headers() {
    const token = get_token();
    return token
      ? {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      }
      : { "Content-Type": "application/json" };
  },
  get_auth_headers() {
    const token = get_token();
    return token ? { Authorization: `Token ${token}` } : {};
  },
  get_dashboard_value(dashboard = {}, keys = [], fallback = 0) {
    const key_list = Array.isArray(keys) ? keys : [keys];
    const found_key = key_list.find(key => ![undefined, null, ""].includes(Reflect.get(dashboard, key)));
    const value = found_key ? Reflect.get(dashboard, found_key) : fallback;
    return Number(value) || 0;
  },
  request_home_stat(options) {
    return new Promise((resolve) => {
      $.ajax({
        ...options,
        success: resolve,
        error: () => resolve(null),
      });
    });
  },
  fetch_home_journey_stats() {
    [
      this.fetch_membership_stats(),
      this.fetch_event_stats(),
      this.fetch_profile_stats(),
      this.fetch_asset_stats(),
      this.fetch_voice_stats(),
      this.fetch_birthday_stats(),
      this.fetch_donation_stats(),
      this.fetch_marketplace_stats(),
    ].forEach((request) => {
      request?.catch?.(() => null);
    });
  },

  fetch_membership_stats() {
    if (!this.active_features?.show_membership) {
      return Promise.resolve();
    }

    return this.request_home_stat({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/membership_management/filter_memberships/`,
      method: "POST",
      headers: this.get_json_headers(),
    }).then((response) => this.apply_membership_stats(response));
  },

  apply_membership_stats(response) {
    const data = Reflect.get(Object(response), "data") || {};
    const dashboard = Reflect.get(Object(data), "dashboard") || {};
    const memberships = Reflect.get(Object(data), "memberships") || [];
    const plans = this.get_dashboard_value(dashboard, "total_count", memberships.length);
    const subscriber_fallback = memberships.reduce((total, membership) => {
      const users = membership.paid_users || membership.subscribed_users || membership.users || [];
      return total + (Array.isArray(users) ? users.length : 0);
    }, 0);
    const subscribers = this.get_dashboard_value(
      dashboard,
      ["total_paid_users", "subscriber_count", "total_subscriptions", "unique_subscribers"],
      subscriber_fallback
    );

    this.set_card_stats("memberships", [
      { label: "plans", value: plans },
      { label: "subscribed", value: subscribers },
    ]);
  },

  fetch_event_stats() {
    if (!this.active_features?.show_events) return Promise.resolve();

    return this.request_home_stat({
      url: this.get_home_event_endpoint(),
      method: "POST",
      headers: this.get_json_headers(),
      data: JSON.stringify(append_active_organization_id({ is_published: true })),
    }).then((response) => this.apply_event_stats(response));
  },

  get_home_event_endpoint() {
    return get_active_organization_id()
      ? `${process.env.VUE_APP_BACKEND_API_URL}/event_management/get_organization_events/`
      : `${process.env.VUE_APP_BACKEND_API_URL}/event_management/filter_events_api/`;
  },

  apply_event_stats(response) {
    const data = response || {};
    const payload = data.organization_data || data.data || {};
    const dashboard = payload.dashboard || data.dashboard || {};
    const events = payload.results || [];
    const upcoming = events.filter(event => this.is_upcoming_event(event)).length;
    const rsvps = events.reduce((total, event) => total + this.get_event_rsvp_count(event), 0);
    const tickets = this.get_dashboard_value(dashboard, ["sold_ticket_instances", "total_orders"]);
    const revenue = this.get_dashboard_value(
      dashboard,
      ["completed_transaction_revenue", "total_revenue"]
    );

    this.set_card_stats("events", [
      { label: "revenue", value: revenue, format: "currency" },
      { label: "upcoming", value: upcoming },
      { label: "RSVPs", value: rsvps },
      { label: "tickets", value: tickets },
    ]);
  },

  fetch_marketplace_stats() {
    if (!this.active_features?.show_marketplace) return Promise.resolve();

    const base_url = `${process.env.VUE_APP_BACKEND_API_URL}/ads_management`;
    const request_data = { page: 1, page_size: 1 };

    return Promise.all([
      this.request_home_stat({
        url: `${base_url}/get_marketplace_products/`,
        method: "GET",
        headers: this.get_json_headers(),
        data: request_data,
      }),
      this.request_home_stat({
        url: `${base_url}/get_marketplace_services/`,
        method: "GET",
        headers: this.get_json_headers(),
        data: request_data,
      }),
    ]).then(([products_response, services_response]) => {
      this.apply_marketplace_stats(products_response, services_response);
    });
  },

  apply_marketplace_stats(products_response, services_response) {
    const product_dashboard = products_response?.dashboard || {};
    const service_dashboard = services_response?.dashboard || {};
    const revenue = this.get_dashboard_value(product_dashboard, "seller_revenue")
      + this.get_dashboard_value(service_dashboard, "seller_revenue");
    const listings = this.get_dashboard_value(product_dashboard, "total_count")
      + this.get_dashboard_value(service_dashboard, "total_count");
    const sold_items = this.get_dashboard_value(product_dashboard, "sold_items")
      + this.get_dashboard_value(service_dashboard, "sold_items");
    const orders = this.get_dashboard_value(product_dashboard, "total_orders")
      + this.get_dashboard_value(service_dashboard, "total_orders");

    this.set_card_stats("marketplace", [
      { label: "revenue", value: revenue, format: "currency" },
      { label: "orders", value: orders },
      { label: "sold", value: sold_items },
      { label: "listings", value: listings },
    ]);
  },

  fetch_admin_stats() {
    if (!get_token() || !this.is_admin_user) return Promise.resolve();

    const endpoint = this.is_organization_admin_user
      ? "get_organization_admins"
      : "get_system_admins_api";

    return this.request_home_stat({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/report_incident_management/${endpoint}/`,
      method: "GET",
      xhrFields: { withCredentials: true },
      headers: this.get_auth_headers(),
    }).then((response) => {
      const admins = this.get_admin_response_items(response);
      this.set_card_stats("admins", [
        { label: "admins", value: admins.length },
      ]);
    });
  },

  is_upcoming_event(event = {}) {
    const end_date = Date.parse(event.end_date || event.start_date || "");
    return !Number.isNaN(end_date) && end_date >= Date.now();
  },

  get_event_rsvp_count(event = {}) {
    const rsvp_list = Reflect.get(Object(event), "rsvp_list");
    if (Array.isArray(rsvp_list)) return rsvp_list.length;
    return Number(Reflect.get(Object(event), "rsvp_count")) || 0;
  },

  fetch_profile_stats() {
    if (!get_token()) return Promise.resolve();

    return Promise.all([
      this.request_home_stat({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_profiles/`,
        method: "POST",
        headers: this.get_json_headers(),
        data: JSON.stringify(this.user_id ? { user_id: this.user_id } : {}),
      }),
      this.request_home_stat({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_type_of_profile/`,
        method: "GET",
        headers: this.get_json_headers(),
      }),
    ]).then(([profiles_response, types_response]) => this.apply_profile_stats(profiles_response, types_response));
  },

  apply_profile_stats(profiles_response, types_response) {
    const profiles = profiles_response?.data?.profiles || [];
    const business_type_id = this.get_business_profile_type_id(types_response);
    const business_profiles = profiles.filter(profile => profile_matches_type(profile, "business", business_type_id));

    this.set_card_stats("profiles", [
      { label: "business", value: business_profiles.length },
    ]);
    this.set_card_stats("companies", [
      { label: "companies", value: business_profiles.length },
    ]);
  },

  get_business_profile_type_id(response) {
    const types = response?.data?.type_of_profile_data || [];
    const business_type = types.find(type => String(type.name || "").toLowerCase() === "business");
    return business_type?.id || null;
  },

  fetch_asset_stats() {
    if (!this.active_features?.show_assets) return Promise.resolve();

    return this.request_home_stat({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/get_community_asset/`,
      method: "POST",
      headers: this.get_json_headers(),
    }).then((response) => this.apply_asset_stats(response));
  },

  apply_asset_stats(response) {
    const dashboard = response?.dashboard || {};
    const assets = response?.community_assets || [];

    this.set_card_stats("assets", [
      { label: "assets", value: this.get_dashboard_value(dashboard, "total_count", assets.length) },
      { label: "bookings", value: this.get_dashboard_value(dashboard, "total_bookings") },
    ]);
  },

  fetch_voice_stats() {
    if (!this.active_features?.show_your_voice || !get_token()) return Promise.resolve();

    return Promise.all([
      this.fetch_discussion_stats(),
      this.fetch_survey_stats(),
      this.fetch_poll_stats(),
    ]).then(([discussions, surveys, polls]) => {
      this.set_card_stats("voice", [
        { label: "discussions", value: discussions },
        { label: "surveys", value: surveys },
        { label: "polls", value: polls },
      ]);
    });
  },

  fetch_discussion_stats() {
    const endpoint = get_active_organization_id() ? "get_organization_topics" : "get_portal_topics";
    return this.request_home_stat({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/portal_management/${endpoint}/`,
      method: "POST",
      headers: this.get_json_headers(),
      data: JSON.stringify(append_active_organization_id({})),
    }).then((response) => {
      const dashboard = response?.dashboard || response?.data?.dashboard || {};
      const topics = response?.topics || response?.data?.topics || response?.data || [];
      return this.get_dashboard_value(dashboard, "total_count", Array.isArray(topics) ? topics.length : 0);
    });
  },

  fetch_survey_stats() {
    const endpoint = get_active_organization_id() ? "get_organization_surveys" : "filter_survey";
    return this.request_home_stat({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/survey_management/${endpoint}/`,
      method: "POST",
      headers: this.get_json_headers(),
      data: JSON.stringify(append_active_organization_id({})),
    }).then((response) => {
      const dashboard = response?.dashboard || response?.data?.dashboard || {};
      const surveys = response?.data?.surveys || response?.data || [];
      return this.get_dashboard_value(dashboard, "total_count", Array.isArray(surveys) ? surveys.length : 0);
    });
  },

  fetch_poll_stats() {
    const endpoint = get_active_organization_id() ? "get_organization_polls" : "filter_poll";
    return this.request_home_stat({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/survey_management/${endpoint}/`,
      method: "POST",
      headers: this.get_json_headers(),
      data: JSON.stringify(append_active_organization_id({})),
    }).then((response) => {
      const dashboard = response?.dashboard || response?.data?.dashboard || {};
      const polls = response?.data?.polls || response?.data || [];
      return this.get_dashboard_value(dashboard, "total_count", Array.isArray(polls) ? polls.length : 0);
    });
  },

  fetch_birthday_stats() {
    if (!this.active_features?.show_upcoming_birthdays) return Promise.resolve();

    return this.request_home_stat({
      url: `${process.env.VUE_APP_BACKEND_API_URL}/system_management/get_upcoming_birthdays/`,
      method: "GET",
      headers: this.get_auth_headers(),
    }).then((response) => {
      const birthdays = response?.data || [];
      this.set_card_stats("birthdays", [
        { label: "upcoming", value: Array.isArray(birthdays) ? birthdays.length : 0 },
      ]);
    });
  },

  format_dashboard_stat_value(stat = {}) {
    const value = Number(stat.value) || 0;
    if (stat.format === "currency") {
      return `R${value.toLocaleString("en-ZA", {
        minimumFractionDigits: value % 1 ? 2 : 0,
        maximumFractionDigits: 2,
      })}`;
    }

    return value.toLocaleString("en-ZA");
  },
};

export default homeJourneyStats;
